import { SearchModel } from '@internal/blocks/SearchResultsBlock/TSearchParams';
import { getMachinesCount } from '@internal/services/apiClient';
import { localeNamespace } from '@internal/utils/machine/i18n/Locale';
import { MachineAttribute } from '@internal/utils/machine/MachineAttribute';
import { MachineType } from '@internal/utils/machine/MachineType';
import { useUserCountryCodeForSearch } from '@internal/utils/user/useUserCountryCodeForSearch';
import { Search } from '@mui/icons-material';
import { Button } from '@renderer-ui-library/atoms';
import {
    BrandSelectFilter,
    MachineTypeSelectFilter,
    TFilterOption,
} from '@renderer-ui-library/molecules';
import { CircularProgress, Grid } from '@renderer-ui-library/mui';
import { OverlayColor } from '@renderer-ui-library/mui/base/useCreateTheme';
import {
    aboveMaxSearchResultsLabel,
    maxSearchResults,
} from 'blocks/SearchResultsBlock/maxSearchResults';
import { rangeFiltersMapToSearchFilters } from 'blocks/SearchResultsBlock/rangeFiltersMapToSearchFilters';
import { TRangeFiltersMap } from 'blocks/SearchResultsBlock/TRangeFiltersMap';
import { useSearchResultsRouting } from 'blocks/SearchResultsBlock/useSearchResultsRouting';
import { translations } from 'i18n/translations';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createMachineFilterEvent } from 'utils/tracking/createMachineFilterEvent';
import { NonSERPMachineFilterEvent } from 'utils/tracking/TTrackingEvent';
import { tracker } from 'utils/tracking/tracker';
import { ModelSelectFilter } from '../ModelSelectFilter';
import { RangeSelectFilter, RangeSelectValue } from '../RangeSelectFilter';
import { sanitizeAppliedRangeFilters } from '../sanitizeAppliedRangeFilters';
import { useSearchCoverBlockRangeFilters } from '../useSearchCoverBlockRangeFilters';

type Props = {
    brand: string | null;
    mappedModel: string | null;
    machineType: MachineType;
    onClose?: () => void;
    colorOverlay: OverlayColor;
    placement: NonSERPMachineFilterEvent['filter_placement'];
};

export const SearchCoverBlockFields: React.FC<Props> = React.memo(props => {
    const { t } = useTranslation(localeNamespace.common);
    const [machineType, setMachineType] = useState<MachineType | null>(
        props.machineType,
    );

    const [brands, setBrands] = useState<TFilterOption[]>([]);
    const [models, setModels] = useState<SearchModel[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [appliedRangeFilters, setAppliedRangeFilters] =
        useState<TRangeFiltersMap>({});
    const [resultsCount, setResultsCount] = useState<number | null>(
        1000 /** just show that we have many of the default machinery type */,
    );
    const { getRangeFilters } = useSearchCoverBlockRangeFilters();
    const rangeFilters = getRangeFilters(machineType);
    const { gotoSearchResults } = useSearchResultsRouting();

    useEffect(() => {
        const sanitizedRangeFilters = sanitizeAppliedRangeFilters(
            rangeFilters,
            appliedRangeFilters,
        );

        if (!isEqual(appliedRangeFilters, sanitizedRangeFilters)) {
            setAppliedRangeFilters(sanitizedRangeFilters);
        }
    }, [rangeFilters, appliedRangeFilters]);

    useEffect(() => {
        if (props.brand) {
            const newBrands = [
                {
                    id: props.brand,
                    label: props.brand,
                },
            ];
            setBrands(newBrands);
        }

        if (props.mappedModel) {
            setModels([
                {
                    name: props.mappedModel,
                    type: 'mapped',
                },
            ]);
        }
    }, [props.brand, props.mappedModel]);

    const abortController = useRef<AbortController | null>(null);
    const { getUserCountryCode } = useUserCountryCodeForSearch();

    const updateSearchResults = useCallback(
        async (params: {
            machineType?: MachineType | null;
            brands: TFilterOption[];
            appliedRangeFilters: TRangeFiltersMap;
            models: SearchModel[];
        }) => {
            if (
                !params.machineType &&
                params.brands.length === 0 &&
                params.models.length === 0 &&
                Object.keys(params.appliedRangeFilters).length === 0
            ) {
                setResultsCount(null);
                return;
            }
            setIsLoading(true);
            abortController.current?.abort();

            abortController.current = new AbortController();
            await getMachinesCount({
                search: {
                    machineType: params.machineType ?? undefined,
                    brands: params.brands.map(brand => brand.id),
                    models: params.models,
                    filters: rangeFiltersMapToSearchFilters(
                        params.appliedRangeFilters,
                    ),
                },
                abortSignal: abortController.current.signal,
                countryCode: await getUserCountryCode(),
            })
                .then(setResultsCount)
                .catch(() => setResultsCount(null))
                .finally(() => setIsLoading(false));
        },
        [getUserCountryCode],
    );

    const onChangeMachineType = useCallback(
        async (machineType: MachineType | null) => {
            setMachineType(machineType);
            updateSearchResults({
                machineType,
                brands,
                models,
                appliedRangeFilters: sanitizeAppliedRangeFilters(
                    getRangeFilters(machineType),
                    appliedRangeFilters,
                ),
            });
        },
        [
            updateSearchResults,
            brands,
            models,
            getRangeFilters,
            appliedRangeFilters,
        ],
    );

    const onChangeBrand = useCallback(
        (brands: TFilterOption[]) => {
            setBrands(brands);
            updateSearchResults({
                machineType,
                brands,
                models,
                appliedRangeFilters,
            });
        },
        [machineType, updateSearchResults, models, appliedRangeFilters],
    );

    const onChangeModel = useCallback(
        (models: SearchModel[]) => {
            setModels(models);
            updateSearchResults({
                machineType,
                brands,
                models,
                appliedRangeFilters,
            });
        },
        [updateSearchResults, machineType, brands, appliedRangeFilters],
    );

    const onChangeRange = useCallback(
        (
            machineAttribute: MachineAttribute,
            value: {
                min?: RangeSelectValue;
                max?: RangeSelectValue;
            },
        ) => {
            const newAppliedRangeFilters: TRangeFiltersMap = {
                ...appliedRangeFilters,
                [machineAttribute]: {
                    ...appliedRangeFilters[machineAttribute],
                    ...value,
                },
            };

            setAppliedRangeFilters(newAppliedRangeFilters);

            updateSearchResults({
                machineType,
                brands,
                models,
                appliedRangeFilters: newAppliedRangeFilters,
            });
        },
        [appliedRangeFilters, machineType, updateSearchResults, brands, models],
    );

    const handleButtonClick = useCallback(() => {
        props.onClose?.();

        tracker.trackEvent(
            createMachineFilterEvent(
                {
                    name: 'filter-applied',
                    filter_placement: props.placement,
                    filter_machine_type: machineType ?? 'any',
                    filter_model: models.map(model => model.name).join(', '),
                },
                {
                    filterBrands: brands.map(brand => brand.id),
                    rangeFilters: appliedRangeFilters,
                },
            ),
        );
        gotoSearchResults(
            {
                machineType: machineType ?? undefined,
                brands: brands.map(brand => brand.id),
                models,
                rangeFilters: appliedRangeFilters,
            },
            false,
        );
    }, [
        machineType,
        // biome-ignore lint/correctness/useExhaustiveDependencies: unknown effect of fix
        props,
        models,
        brands,
        appliedRangeFilters,
        gotoSearchResults,
    ]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
                <MachineTypeSelectFilter
                    selectedMachineType={machineType}
                    onChange={onChangeMachineType}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <BrandSelectFilter
                    selectedMachineType={machineType}
                    selectedBrands={brands}
                    onChange={onChangeBrand}
                    closeOnSelect
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <ModelSelectFilter
                    onChange={onChangeModel}
                    brands={brands.map(brand => brand.id)}
                    closeOnSelect
                    models={models}
                    machineType={machineType}
                    showSelectedFuzzyModel
                />
            </Grid>

            {rangeFilters.map(rangeFilter => (
                <Grid
                    item
                    xs={12}
                    md={rangeFilters.length === 3 ? 3 : 4}
                    key={rangeFilter.machineAttribute}
                >
                    <RangeSelectFilter
                        onChange={onChangeRange}
                        config={rangeFilter}
                        min={
                            appliedRangeFilters[rangeFilter.machineAttribute]
                                ?.min
                        }
                        max={
                            appliedRangeFilters[rangeFilter.machineAttribute]
                                ?.max
                        }
                        disabled={!machineType}
                        testId={`search-cover-block-range-filter-${rangeFilter.machineAttribute}`}
                    />
                </Grid>
            ))}

            <Grid item xs={12} md={rangeFilters.length === 3 ? 3 : 4}>
                <Button
                    size="large"
                    color="secondary"
                    fullWidth
                    startIcon={
                        isLoading ? (
                            <CircularProgress size={22} color="inherit" />
                        ) : (
                            <Search />
                        )
                    }
                    onClick={handleButtonClick}
                    data-testid="search-cover-block-button"
                >
                    {resultsCount === null
                        ? t(translations.searchCoverBlockButtonTextSearch)
                        : t(translations.searchCoverBlockButtonText, {
                              count: resultsCount,
                              amount:
                                  resultsCount >= maxSearchResults
                                      ? aboveMaxSearchResultsLabel
                                      : resultsCount,
                          })}
                </Button>
            </Grid>
        </Grid>
    );
});

SearchCoverBlockFields.displayName = 'SearchCoverBlockFields';
